import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    // Cargar usuario al iniciar
    const loadUser = useCallback(async () => {
        const currentToken = localStorage.getItem('token');
        if (!currentToken) {
            // 🔓 MODO LIBRE: Si no hay token, creamos un usuario "Invitado Premium"
            // Esto asegura que incluso sin login, la app funcione full.
            console.log("🔓 AuthContext: No token found, granting Guest Premium access.");
            setUser({
                _id: 'guest-premium',
                name: 'Invitado Premium',
                email: 'guest@matica.com',
                role: 'admin' // Forzamos rol admin
            });
            setLoading(false);
            return;
        }

        try {
            // Usar apiFetch que ya maneja la URL base
            const data = await apiFetch('/api/auth/user', {
                headers: { 'x-auth-token': currentToken }
            });

            if (data && (data.user || data.email)) {
                // 🔓 MODO LIBRE: Forzamos el rol a admin en el usuario cargado
                const loadedUser = data.user || data;
                loadedUser.role = 'admin';
                console.log("🔓 AuthContext: User loaded and upgraded to ADMIN.");
                setUser(loadedUser);
            } else {
                // Token inválido, pero damos acceso invitado premium
                console.log("🔓 AuthContext: Invalid token, falling back to Guest Premium.");
                setUser({
                    _id: 'guest-premium',
                    name: 'Invitado Premium',
                    email: 'guest@matica.com',
                    role: 'admin'
                });
            }
        } catch (error) {
            console.error('Error loading user:', error);
            // En caso de error, también damos acceso invitado premium
            console.log("🔓 AuthContext: Error loading user, granting Guest Premium access.");
            setUser({
                _id: 'guest-premium',
                name: 'Invitado Premium',
                email: 'guest@matica.com',
                role: 'admin'
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    const login = async (email, password) => {
        const res = await apiFetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        });

        // El backend devuelve { token: "...", user: { ... } }
        if (res.token) {
            localStorage.setItem('token', res.token);
            setToken(res.token);
            // 🔓 MODO LIBRE: Forzamos rol admin al hacer login
            const loggedUser = res.user;
            loggedUser.role = 'admin';
            setUser(loggedUser);
            return loggedUser;
        }
        throw new Error('No token received');
    };

    const register = async (userData) => {
        const res = await apiFetch('/api/auth/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData)
        });

        if (res.token) {
            localStorage.setItem('token', res.token);
            setToken(res.token);
            // 🔓 MODO LIBRE: Forzamos rol admin al registrarse
            const registeredUser = res.user;
            registeredUser.role = 'admin';
            setUser(registeredUser);
            return registeredUser;
        }
        return res;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        // Al hacer logout, volvemos a invitado premium en lugar de null
        setUser({
            _id: 'guest-premium',
            name: 'Invitado Premium',
            email: 'guest@matica.com',
            role: 'admin'
        });
    };

    const updateProfile = async (data) => {
        // Implementar si existe el endpoint
        // const res = await apiFetch('/api/users/profile', { method: 'PUT', body: JSON.stringify(data) ... });
        // setUser(res);
    };

    // Lógica de permisos (simplificada para este contexto)
    const hasFeatureAccess = (featureName) => {
        // 🔓 MODO LIBRE: SIEMPRE TRUE
        return true;
    };

    const value = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        updateProfile,
        hasFeatureAccess,
        // 🔓 MODO LIBRE: Siempre autenticado y siempre premium
        isAuthenticated: true,
        isPremium: true
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
